<template>
    <component
        :is="isLink ? BaseLink : 'button'"
        v-bind="linkBindings"
        :class="[$style.button, $style[`width_${props.width}`], $style[`color_${props.color}`]]"
        :type="props.type"
        :disabled="props.disabled"
        :data-interface-target="interfaceTarget"
        @click="props.onClick"
        @pointerdown="props.onPointerdown"
    >
        <div :class="$style.content">
            <slot />
        </div>
    </component>
</template>

<script lang="ts" setup>
import BaseLink from '@/components_new/BaseLink.vue'

import { ButtonOptions, useButton } from '../composables/use-button'

export type BaseButtonProps = ButtonOptions

const props = withDefaults(defineProps<BaseButtonProps>(), {
    type: 'button',
    size: 'm',
    width: 'normal',
    color: 'purple-400',
})

const { isLink, linkBindings } = useButton(props)
</script>
<style module>
.button {
    display: inline-block;
    line-height: 100%;
    box-sizing: border-box;
    position: relative;
    cursor: pointer;
    white-space: nowrap;
    border-radius: var(--border-radius);
    --border-width: 2px;
    --border-radius: 1000px;
    --focus-accent: var(--electric-lime);
    transition: background-color 0.15s;
}

.content {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
}

.color_purple-400 {
    --text: #fff;
    --main: var(--purple-400);
    --gradient: var(--purple-400-linear-gradient);
}

.color_purple-400-flat {
    --text: #fff;
    --main: var(--purple-400);
    --gradient: var(--purple-400-flat-linear-gradient);
}

.color_purple-500 {
    --text: #fff;
    --main: #aa76ff;
    --filter: brightness(1.2);
}

.color_purple-600 {
    --text: #fff;
    --main: #c7abff;
    --filter: brightness(1.2);
}

.color_purple-graphite {
    --text: #fff;
    --main: var(--experimental-purple-graphit);
    --main2: var(--purple-400);
    --gradient: var(--purple-graphite-linear-gradient);
}

.color_golden {
    --text: #000;
    --main: #ffc850;
    --filter: brightness(1.2);
}

.color_white-60 {
    --text: #000;
    --main: var(--white-60);
    --filter: brightness(1.2);
}

.color_white {
    --text: #000;
    --main: var(--white);
    --border-width: 1px;
    --filter: brightness(1.2);
}

.color_white-08 {
    --text: #fff;
    --main: var(--white-08);
    --border-width: 1px;
    --filter: brightness(1.2);
}

.color_white-20 {
    --text: #000;
    --main: var(--white-20);
    --border-width: 1px;
    --filter: brightness(1.2);
}

.color_carbon-200 {
    --text: #fff;
    --main: var(--carbon-200);
    --filter: brightness(1.2);
}

.color_carbon-300 {
    --text: var(--purple-500);
    --main: var(--carbon-300);
    --filter: brightness(1.2);
}

.color_carbon-400 {
    --text: #fff;
    --main: var(--carbon-400);
    --filter: brightness(1.2);
}

.color_lime {
    --text: var(--black);
    --main: var(--electric-lime);
    --focus-accent: var(--purple-400);
    --filter: brightness(1.2);
}

.color_lavander-300 {
    --text: var(--black);
    --main: var(--lavander-300);
    --filter: brightness(1.2);
}

.color_rose-100 {
    --text: var(--rose-400);
    --main: var(--rose-100);
    --focus-accent: var(--white-08);
    --filter: brightness(1.2);
}

.color_rose-400 {
    --text: var(--black);
    --main: var(--rose-400);
    --focus-accent: var(--white-08);
    --filter: brightness(1.2);
}

.color_salad {
    --text: var(--black);
    --main: #9ce52e;
    --focus-accent: var(--purple-400);
    --gradient: var(--salad-linear-gradient);
    --box-shadow: var(--salad-box-shadow);
}

.button:before,
.button:after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    border-radius: var(--border-radius);
}

.button:before {
    background: var(--background-color, transparent);
}

.button:after {
    border: var(--border-width) solid var(--border-color, transparent);
}

.button:focus-visible {
    outline: none;
    --border-color: var(--focus-accent);
}

:disabled {
    cursor: not-allowed;
}

.button.color_purple-graphite:not(:disabled):hover:before {
    filter: none;
    --background-color: var(--main2);
}

.button.color_carbon-300:not(:disabled):hover,
.button.color_carbon-300:not(:disabled):active,
.button.color_carbon-300:not(:disabled):focus-visible {
    color: var(--black);
    --main: var(--electric-lime);
}

.width_normal {
    width: auto;
}

.width_full {
    width: 100%;
}
</style>
